import { useCallback, useState } from 'react'
import { Images } from '../../modules/API/Images/images'
import {
  IImageObjectRef,
  IImageUploadPayload,
} from '../../modules/API/Images/models/imageObject'

export interface UsePhotoUploadReturn {
  /** null means the upload modal is closed. */
  target: IImageObjectRef | null
  openPhotoUpload: (ref: IImageObjectRef) => void
  closePhotoUpload: () => void
  uploadPhoto: (payload: IImageUploadPayload) => Promise<void>
  isPhotoUploaded: (ref: IImageObjectRef) => boolean
}

/**
 * Server errors are contextual and not meant for end users, so whatever comes
 * back is replaced by one readable message.
 */
const GENERIC_ERROR = 'משהו השתבש, נסו שוב'

// All three parts belong to the key: the same delivery number with a different
// sub-id is a different row, and the id alone means nothing without its type.
const buildKey = (ref: IImageObjectRef): string =>
  `${ref.ObjectType}:${ref.ObjectID}:${ref.ObjectSubID}`

/**
 * Owns the photo-upload target and remembers which objects already got a photo.
 * Domain-agnostic: the caller decides what the object type means. The marks live
 * until the page reloads — there is no storage yet.
 */
export const usePhotoUpload = (): UsePhotoUploadReturn => {
  const [target, setTarget] = useState<IImageObjectRef | null>(null)
  const [uploadedKeys, setUploadedKeys] = useState<Set<string>>(() => new Set())

  const openPhotoUpload = useCallback((ref: IImageObjectRef) => {
    setTarget(ref)
  }, [])

  const closePhotoUpload = useCallback(() => {
    setTarget(null)
  }, [])

  // Rejection reaches PhotoUploadPanel, which renders it as the error card.
  const uploadPhoto = useCallback(
    async (payload: IImageUploadPayload) => {
      if (!target) throw new Error('לא נבחרה שורה להעלאת תמונה')
      try {
        await Images.uploadImages(target, payload)
      } catch {
        throw new Error(GENERIC_ERROR)
      }
      setUploadedKeys((prev) => new Set(prev).add(buildKey(target)))
    },
    [target],
  )

  const isPhotoUploaded = useCallback(
    (ref: IImageObjectRef) => uploadedKeys.has(buildKey(ref)),
    [uploadedKeys],
  )

  return { target, openPhotoUpload, closePhotoUpload, uploadPhoto, isPhotoUploaded }
}
